import type { MetadataProps } from './domain';

type Icon = {
  kind: string;
  value: string;
};

type MentionedBy = {
  name: string;
  memberId?: string;
};

type Mention = {
  at: Date;
  others?: number;
  by: MentionedBy;
};

export type Tasks = {
  taskId: string;
  title: { type: string; value: string }[];
};

export type Person = {
  memberId: string;
  name: string;
};

type Message = {
  text: string;
  persons: Person[];
  tasks?: Tasks[];
};

enum ElementType {
  Text = 'text',
}

export type TaskState = 'COMPLETED' | 'ARCHIVED' | 'ACTIVE' | 'DELETED';

export enum ActivityCardTypeEnum {
  PostMentions = 'POST_MENTIONS',
  FlowActivity = 'FLOW_ACTIVITY',
  TaskActivities = 'TASK_ACTIVITIES',
  OwnerPostReply = 'OWNER_POST_REPLY',
  ResponseMentions = 'RESPONSE_MENTIONS',
  PostThreadReplies = 'POST_THREAD_REPLIES',
  FlowAccessRequest = 'FLOW_ACCESS_REQUEST',
  OwnerResponseReply = 'OWNER_RESPONSE_REPLY',
  RecognitionReminders = 'RECOGNITION_REMINDERS',
  DirectReportActivity = 'DIRECT_REPORT_ACTIVITY',
  ResponseThreadReplies = 'RESPONSE_THREAD_REPLIES',
  FlowAccessGranted = 'FLOW_ACCESS_REQUEST_GRANTED',
  BirthdayCelebrationActivity = 'BIRTHDAY_CELEBRATION_ACTIVITY',
  AnniversaryCelebrationActivity = 'ANNIVERSARY_CELEBRATION_ACTIVITY',
  RecognitionDirectReportActivity = 'RECOGNITION_DIRECT_REPORT_ACTIVITY',
  BirthdayCelebrationDirectReport = 'BIRTHDAY_CELEBRATION_DIRECT_REPORT_ACTIVITY',
  AnniversaryCelebrationDirectReport = 'ANNIVERSARY_CELEBRATION_DIRECT_REPORT_ACTIVITY',
}

export enum FlowAccessRequestStatus {
  Viewer = 'VIEWER',
  Participant = 'PARTICIPANT',
  ParticipantAndViewer = 'PARTICIPANT_AND_VIEWER',
}

type TextElement = {
  type: ElementType;
  value: string;
};

export type FlowAccessRequestActivityType = {
  type: ActivityCardTypeEnum.FlowAccessRequest;
  entity: {
    flowId: string;
    name: string;
    icon: Icon;
    shareRequestId: string;
    for: FlowAccessRequestStatus;
    invitedBy: MentionedBy;
    invitees: {
      name: string;
      others: number;
      memberId: string;
    };
  };
};

export type RecognitionRemindersActivityType = {
  type: ActivityCardTypeEnum.RecognitionReminders;
};

export type TaskActivityType = {
  type: ActivityCardTypeEnum.TaskActivities;
  entity: {
    taskId: string;
    state: TaskState;
    title: TextElement[];
    transactionId: string;
    assignedBy?: MentionedBy;
    assignedTo?: MentionedBy;
    description: TextElement[];
    stateUpdatedBy?: MentionedBy;
  };
};

export type FlowAccessGrantedActivityType = {
  type: ActivityCardTypeEnum.FlowAccessGranted;
  entity: {
    icon: Icon;
    name: string;
    flowId: string;
    ownerId: string;
    invitedBy: MentionedBy;
    for: FlowAccessRequestStatus;
  };
};

export type AnniversaryCelebrationActivityType = {
  type: ActivityCardTypeEnum.AnniversaryCelebrationActivity;
  entity: {
    postId: string;
    person: string;
    others: number;
    message: string;
    memberId: string;
    isMuted: boolean;
  };
};

export type BirthdayCelebrationActivityType = {
  type: ActivityCardTypeEnum.BirthdayCelebrationActivity;
  entity: {
    postId: string;
    person: string;
    others: number;
    message: string;
    memberId: string;
    isMuted: boolean;
  };
};

export type ResponseThreadReplyActivityType = {
  type: ActivityCardTypeEnum.ResponseThreadReplies;
  entity: {
    flowId: string;
    name: string;
    icon: Icon;
    lastReply: {
      text: string;
      gifUrl?: string;
      commentId: string;
      persons: Person[];
      from: {
        memberId: string;
        name: string;
      };
    };
    mention?: Mention;
    reply?: {
      at: Date;
      others: number;
      by: MentionedBy;
    };
    response: {
      name: string;
      memberId: string;
      responseId: string;
    };
    isMuted: boolean;
    hideReplies?: boolean;
    hideReactions?: boolean;
    showTurnOffSettings?: boolean;
  };
};

export type PostThreadReplyActivityType = {
  type: ActivityCardTypeEnum.PostThreadReplies;
  entity: {
    flowId: string;
    name: string;
    post: {
      postId: string;
      name: string;
      memberId: string;
    };
    lastReply: {
      text: string;
      commentId: string;
      gifUrl?: string;
      persons: Person[];
      from: {
        memberId: string;
        name: string;
      };
    };
    reply?: {
      at: Date;
      others: number;
      by: MentionedBy;
    };
    to: MentionedBy;
    mention?: Mention;
    isMuted: boolean;
  };
};

export type OwnerResponseReplyActivityType = {
  type: ActivityCardTypeEnum.OwnerResponseReply;
  entity: {
    flowId: string;
    name: string;
    icon: Icon;
    lastReply: {
      commentId: string;
      from: {
        memberId: string;
        name: string;
      };
      persons: Person[];
      text: string;
      gifUrl?: string;
    };
    reply: {
      at: Date;
      by: MentionedBy;
      others: number;
    };
    response: {
      name: string;
      memberId: string;
      responseId: string;
    };
    isMuted: boolean;
    hideReplies?: boolean;
    hideReactions?: boolean;
    showTurnOffSettings?: boolean;
  };
};

export type OwnerPostReplyActivityType = {
  type: ActivityCardTypeEnum.OwnerPostReply;
  entity: {
    post: {
      postId: string;
      name: string;
      memberId: string;
    };
    lastReply: {
      text: string;
      commentId: string;
      gifUrl?: string;
      persons: Person[];
      from: {
        memberId: string;
        name: string;
      };
    };
    reply: {
      at: Date;
      by: MentionedBy;
      others: number;
    };
    to: MentionedBy;
    isMuted: boolean;
  };
};

export type FlowEntityActivityType = {
  type: ActivityCardTypeEnum.FlowActivity;
  entity: {
    icon: Icon;
    name: string;
    endAt?: string;
    flowId: string;
    ownerId: string;
    canPost: boolean;
    countUnreadPosts?: number;
    responded?: { by: MentionedBy; others: number };
  };
};

export type ResponseMentionsActivityType = {
  type: ActivityCardTypeEnum.ResponseMentions;
  entity: {
    flowId: string;
    name: string;
    icon: Icon;
    responseId: string;
    mention: Mention;
    message: Message;
    isMuted: boolean;
    hideReplies?: boolean;
    hideReactions?: boolean;
    showTurnOffSettings?: boolean;
  };
};

export type PostMentionsActivityType = {
  type: ActivityCardTypeEnum.PostMentions;
  entity: {
    postId: string;
    name: string;
    mention: Mention;
    message: Message;
    isMuted: boolean;
  };
};

export type DirectReportActivityType = {
  type: ActivityCardTypeEnum.DirectReportActivity;
  entity: {
    canPost: boolean;
    flowId: string;
    icon: Icon;
    name: string;
    ownerId: string;
    countUnreadPosts?: number;
    responded?: { by: MentionedBy; others: number };
  };
};

export type DirectReportBirthdayCelebrationActivityType = {
  type: ActivityCardTypeEnum.BirthdayCelebrationDirectReport;
  entity: {
    postId: string;
    person: Person;
    message: string;
  };
};

export type DirectReportAnniversaryCelebrationActivityType = {
  type: ActivityCardTypeEnum.AnniversaryCelebrationDirectReport;
  entity: {
    postId: string;
    person: Person;
    message: string;
  };
};

export type DirectReportRecognisedActivityType = {
  type: ActivityCardTypeEnum.RecognitionDirectReportActivity;
  entity: {
    postId: string;
    person: Person;
    others: number;
    message: string;
    mention: Mention;
  };
};

export type ActivityEntity =
  | TaskActivityType
  | FlowEntityActivityType
  | PostMentionsActivityType
  | DirectReportActivityType
  | OwnerPostReplyActivityType
  | PostThreadReplyActivityType
  | ResponseMentionsActivityType
  | FlowAccessGrantedActivityType
  | FlowAccessRequestActivityType
  | OwnerResponseReplyActivityType
  | ResponseThreadReplyActivityType
  | BirthdayCelebrationActivityType
  | RecognitionRemindersActivityType
  | DirectReportRecognisedActivityType
  | AnniversaryCelebrationActivityType
  | DirectReportBirthdayCelebrationActivityType
  | DirectReportAnniversaryCelebrationActivityType;

export type ActivityCardType = {
  state: string;
  cardId: string;
  updatedAt: Date;
} & ActivityEntity;

export type ImportantActivitiesApiResponse = {
  data: {
    total: string;
    metadata?: MetadataProps;
    data: ActivityCardType[];
  };
};

export type ImportantActivitiesByIdApiResponse = ActivityCardType;
